import React, { useState } from 'react';
import { X, RefreshCw, ChevronDown } from 'lucide-react';

/**
 * GoogleFormsSyncModal
 * Shows Google Form column headers and lets the org user map
 * Name / Email / Phone columns, then confirms sync.
 *
 * Props:
 *  headers          – string[]  — all column names from the linked sheet
 *  suggestedMapping – { name_column?, email_column?, phone_column? }
 *  tempToken        – string    — opaque server-side token (never a credential)
 *  activityId       – string
 *  onConfirm        – (fieldMapping) => void   (fieldMapping = {name_column, email_column, phone_column})
 *  onClose          – () => void
 *  loading          – bool
 */
export default function GoogleFormsSyncModal({
  headers = [],
  suggestedMapping = {},
  tempToken,
  activityId,
  onConfirm,
  onClose,
  loading = false,
}) {
  const [nameCol,  setNameCol]  = useState(suggestedMapping.name_column  || '');
  const [emailCol, setEmailCol] = useState(suggestedMapping.email_column || '');
  const [phoneCol, setPhoneCol] = useState(suggestedMapping.phone_column || '');

  // Columns that aren't mapped to name/email/phone
  const mapped = new Set([nameCol, emailCol, phoneCol].filter(Boolean));
  const otherCols = headers.filter((h) => !mapped.has(h));

  const handleConfirm = () => {
    const fieldMapping = {};
    if (nameCol)  fieldMapping.name_column  = nameCol;
    if (emailCol) fieldMapping.email_column = emailCol;
    if (phoneCol) fieldMapping.phone_column = phoneCol;
    onConfirm(fieldMapping);
  };

  const PLACEHOLDER = '— Select column —';

  const ColDropdown = ({ label, value, onChange, required }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none px-3 py-2.5 pr-9 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
          data-testid={`sync-map-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <option value="">{PLACEHOLDER}</option>
          {headers.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      data-testid="google-forms-sync-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              <RefreshCw size={15} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Map Form Fields</h2>
              <p className="text-xs text-gray-400 mt-0.5">{headers.length} column{headers.length !== 1 ? 's' : ''} found</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="sync-modal-close"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-xs text-gray-500">
            Select which columns contain the participant's name, email, and phone. We've pre-selected
            suggestions — adjust as needed.
          </p>

          {/* Mapped fields */}
          <div className="space-y-3 p-3.5 bg-gray-50 rounded-xl border border-gray-100">
            <ColDropdown label="Name"  value={nameCol}  onChange={setNameCol}  />
            <ColDropdown label="Email" value={emailCol} onChange={setEmailCol} />
            <ColDropdown label="Phone / WhatsApp" value={phoneCol} onChange={setPhoneCol} />
          </div>

          {/* Other columns */}
          {otherCols.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Other form fields (all captured)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {otherCols.map((col) => (
                  <span
                    key={col}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100"
                  >
                    {col}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Guidance note */}
          <p className="text-xs text-gray-400 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
            All existing and future responses will be synced. Duplicates are automatically skipped.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-2.5 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="sync-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="sync-modal-confirm"
          >
            {loading ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Starting Sync…
              </>
            ) : (
              'Confirm & Sync'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
